import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { permissionGroups, roleCreateSchema, type PermissionKey, type RoleSummary } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, Skeleton, TextAreaField, TextField, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, del, get, patch, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';

const errText = (e: unknown) => (e instanceof ApiClientError ? `${e.message}${e.requestId ? ` (Ref ${e.requestId})` : ''}` : 'Something went wrong. Try again.');

type Editing = { mode: 'view'; role: RoleSummary } | { mode: 'create'; from?: RoleSummary } | null;

export function RolesPage() {
  const { can } = useAuth();
  const [editing, setEditing] = useState<Editing>(null);
  const q = useQuery<RoleSummary[], ApiClientError>({ queryKey: ['roles'], queryFn: () => get('/roles') });
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Roles and permissions</h1>
          <p>A role is a named set of permissions. Built-in roles are fixed; duplicate one to make a custom role. Changes apply to everyone in the role immediately.</p>
        </div>
        {can('roles.manage') ? <div className="pagehead__actions"><Button variant="primary" icon="plus" onClick={() => setEditing({ mode: 'create' })}>New role</Button></div> : null}
      </div>
      {q.isLoading ? <Skeleton width="100%" height={220} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? <EmptyState title="No roles" icon="users" /> : (
        <div className="ag-table-wrap" {...scrollRegion('Roles')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Roles</caption>
            <thead><tr><th scope="col">Role</th><th scope="col">Type</th><th scope="col" className="ag-num">Permissions</th><th scope="col" className="ag-num">People</th><th scope="col"><span className="ag-visually-hidden">Actions</span></th></tr></thead>
            <tbody>
              {q.data!.map((r) => (
                <tr key={r.id}>
                  <td><span className="ag-cell-2"><span>{r.name}</span><span>{r.description ?? r.key}</span></span></td>
                  <td><Badge tone={r.system ? 'neutral' : 'accent'}>{r.system ? 'Built-in' : 'Custom'}</Badge></td>
                  <td className="ag-num">{r.permissions.length}</td>
                  <td className="ag-num">{r.userCount}</td>
                  <td style={{ textAlign: 'right' }}><Button size="sm" onClick={() => setEditing({ mode: 'view', role: r })} aria-label={`${r.system || !can('roles.manage') ? 'View' : 'Edit'} ${r.name}`}>{r.system || !can('roles.manage') ? 'View' : 'Edit'}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RoleDrawer editing={editing} onClose={() => setEditing(null)} onDuplicate={(r) => setEditing({ mode: 'create', from: r })} />
    </>
  );
}

function RoleDrawer({ editing, onClose, onDuplicate }: { editing: Editing; onClose: () => void; onDuplicate: (r: RoleSummary) => void }) {
  // Remount per target so form state never leaks between roles.
  const key = editing ? (editing.mode === 'view' ? `v-${editing.role.id}` : `c-${editing.from?.id ?? 'new'}`) : 'closed';
  const title = !editing ? 'Role' : editing.mode === 'create' ? (editing.from ? `New role based on ${editing.from.name}` : 'New role') : editing.role.name;
  return (
    <Drawer open={!!editing} onClose={onClose} title={title} footer={null}>
      {editing ? <RoleForm key={key} editing={editing} onClose={onClose} onDuplicate={onDuplicate} /> : null}
    </Drawer>
  );
}

function RoleForm({ editing, onClose, onDuplicate }: { editing: NonNullable<Editing>; onClose: () => void; onDuplicate: (r: RoleSummary) => void }) {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const role = editing.mode === 'view' ? editing.role : null;
  const creating = editing.mode === 'create';
  const readOnly = !can('roles.manage') || (role?.system ?? false);
  const [name, setName] = useState(role?.name ?? (editing.mode === 'create' && editing.from ? `${editing.from.name} (custom)` : ''));
  const [roleKey, setRoleKey] = useState(editing.mode === 'create' && editing.from ? `${editing.from.key}-custom` : '');
  const [description, setDescription] = useState(role?.description ?? (editing.mode === 'create' ? (editing.from?.description ?? '') : ''));
  const [perms, setPerms] = useState<Set<PermissionKey>>(new Set(role?.permissions ?? (editing.mode === 'create' ? (editing.from?.permissions ?? []) : [])));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const mine = useMemo(() => new Set<string>(user?.permissions ?? []), [user]);
  const isOwner = user?.roleKey === 'owner';

  const done = async (message: string) => { await qc.invalidateQueries({ queryKey: ['roles'] }); await qc.invalidateQueries({ queryKey: ['role-options'] }); toast({ tone: 'success', message }); onClose(); };
  const fail = (e: unknown) => {
    if (e instanceof ApiClientError && e.fieldErrors.length) setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
    setError(errText(e));
  };
  const save = useMutation({
    mutationFn: () => {
      const permissions = [...perms];
      if (creating) {
        const p = roleCreateSchema.safeParse({ key: roleKey, name, description: description || undefined, permissions });
        if (!p.success) { setErrors(Object.fromEntries(p.error.issues.map((i) => [String(i.path[0]), i.message]))); throw new Error('invalid'); }
        return post('/roles', p.data);
      }
      return patch(`/roles/${role!.id}`, { name, description: description || null, permissions });
    },
    onSuccess: () => done(creating ? `Created ${name}.` : `Saved ${name}. People in this role were signed in again with the new access.`),
    onError: (e) => { if (!(e instanceof Error && e.message === 'invalid')) fail(e); },
  });
  const remove = useMutation({ mutationFn: () => del(`/roles/${role!.id}`), onSuccess: () => done(`Deleted ${role!.name}.`), onError: fail });

  const toggle = (p: PermissionKey) => setPerms((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });
  // Only rights the editor already holds can be added (the server enforces this too); removing is always allowed.
  const locked = (p: PermissionKey) => !isOwner && !mine.has(p) && !perms.has(p);

  return (
    <form noValidate className="formgrid" onSubmit={(e) => { e.preventDefault(); setError(null); setErrors({}); save.mutate(); }}>
      {error ? <Notice tone="error" title="Could not save">{error}</Notice> : null}
      {role?.system ? <Notice tone="info" title="Built-in role" live={false}>This role is part of the product and can’t be edited. Duplicate it to start a custom role from the same permissions.</Notice> : null}
      <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} readOnly={readOnly} error={errors.name} />
      {creating ? <TextField label="Key" required value={roleKey} onChange={(e) => setRoleKey(e.target.value)} hint="Lowercase letters, digits and hyphens. Can’t be changed later." error={errors.key} /> : <p className="ag-muted ag-mono">key: {role!.key}</p>}
      <TextAreaField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} readOnly={readOnly} rows={2} error={errors.description} />
      <fieldset className="permset">
        <legend>Permissions ({perms.size})</legend>
        {permissionGroups.map((g) => (
          <div key={g.group} className="permset__group">
            <p className="permset__title">{g.group}</p>
            {g.keys.map((p) => (
              <label key={p} className="permset__item" data-locked={locked(p) || undefined}>
                <input type="checkbox" checked={perms.has(p)} disabled={readOnly || locked(p)} onChange={() => toggle(p)} />
                <span className="ag-mono">{p}</span>
                {locked(p) && !readOnly ? <span className="ag-muted"> · you don’t hold this</span> : null}
              </label>
            ))}
          </div>
        ))}
      </fieldset>
      <div className="actions__row">
        {!readOnly ? <Button type="submit" variant="primary" loading={save.isPending}>{creating ? 'Create role' : 'Save changes'}</Button> : null}
        {role && can('roles.manage') ? <Button onClick={() => onDuplicate(role)}>Duplicate</Button> : null}
        {role && !role.system && can('roles.manage') ? (
          confirmDelete ? <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>Confirm delete</Button> : <Button variant="ghost" onClick={() => setConfirmDelete(true)} disabled={role.userCount > 0} title={role.userCount > 0 ? 'Move its people to another role first' : undefined}>Delete…</Button>
        ) : null}
        <Button onClick={onClose}>Close</Button>
      </div>
    </form>
  );
}
